<template>
  <ReservationHero v-if="websiteStatus == 1" />

  <div v-else class="landing">
    <template v-for="s in visibleSections" :key="s.key">
      <component
        v-if="s.type !== 'cta' || !auth"
        :is="sectionRegistry[s.type]"
        v-bind="sectionProps(s)"
      />
      <MyTestimonialEditor
        v-if="s.type === 'testimonials' && auth"
        :my-testimonial="myTestimonial"
        @saved="onTestimonialSaved"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';

import ReservationHero      from '@/components/home/ReservationHero.vue';
import MyTestimonialEditor  from '@/components/home/MyTestimonialEditor.vue';
import { sectionRegistry }  from '@/components/home/sectionRegistry';
import { useTestimonials }  from '@/composables/useTestimonials';
import { useHomepageSections } from '@/composables/useHomepageSections';

const store = useStore();

const websiteStatus = ref(localStorage.getItem('website_status') || 0);
const auth = ref(store.getters['isAuthenticated']);
const user = ref(JSON.parse(localStorage.getItem('user') || 'null'));

const {
  testimonials,
  testimonialsLoading,
  myTestimonial,
  fetchTestimonials,
  fetchMyTestimonial,
} = useTestimonials();

const { visibleSections, fetchHomepageSections } = useHomepageSections();

// Each section's `type` needs slightly different props beyond its own
// `settings` blob — home_products needs `kind` pulled OUT of settings (it's
// the same component rendered twice, once per home_products row); hero/
// quick_access still need the auth/user context that used to be hardcoded
// in this template; testimonials keeps its existing separately-fetched data
// (own loading/skeleton state, unrelated to the sections fetch).
function sectionProps(s) {
  const settings = s.settings || {};
  switch (s.type) {
    case 'hero':
      return { settings, auth: auth.value, user: user.value };
    case 'quick_access':
      return { settings, auth: auth.value };
    case 'home_products':
      return { settings, kind: settings.kind };
    case 'testimonials':
      return { settings, testimonials: testimonials.value, loading: testimonialsLoading.value };
    default:
      return { settings };
  }
}

function onTestimonialSaved(updatedData) {
  myTestimonial.value = updatedData;
  fetchTestimonials();
}

onMounted(() => {
  fetchHomepageSections();
  fetchTestimonials();
  if (auth.value) fetchMyTestimonial();
});
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
.landing { font-family: 'Cairo', sans-serif; direction: rtl; overflow-x: hidden; }

/* Mobile app home: keep a light app background behind the compact sections.
   Clearing the fixed bottom nav is now handled once, correctly, by the
   global `body { padding-bottom: ... }` rule in style.min.css (sized to
   BottomNav's actual rendered height including the safe-area) — this used
   to duplicate that same ~74px reservation here too, stacking extra blank
   space between the last section and Footer for no reason tied to the
   bottom nav. Small fixed cushion kept, not tied to bottom-nav height. */
@media (max-width: 768px) {
  .landing {
    background: #F5F7FB;
    padding-bottom: 8px;
  }
}
</style>
